import * as Sentry from '@sentry/ember';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — shared `routeDidChange` wiring installed into every
 *              app Router (GA page_view, optional GTM push, scroll-to-top,
 *              dropdown cleanup, Sentry route tag)
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      @sentry/ember (Sentry.setTag)
 *   siblings:  -
 *   tests:     router-tracking-test.js
 *   gotchas:   `stripToken` must stay enabled for public-shop apps (booking /
 *              e-commerce): their deep links carry a live bearer token
 *              (`?access_token=…`) that must never reach GA/GTM.
 *
 * Called once from each app's Router constructor via `setupRouterTracking(this, options)`.
 */
function stripAccessToken(url) {
  if (!url || !url.includes('access_token')) {
    return url;
  }
  return url.replace(/([?&])access_token=[^&]*&?/g, '$1').replace(/[?&]$/, '');
}

/**
 * Call from the Router constructor (after `super`):
 *
 *   setupRouterTracking(this, { gaId: 'G-…' });
 *
 * @param {EmberRouter} router - the Router instance (`this`); needs the
 *   injected `router` service on it (optional-chained for unit-test containers
 *   that have no service — see commit 9212b3c).
 * @param {object} options
 * @param {string} options.gaId - GA4 measurement id for gtag page_view.
 * @param {string[]} [options.skipPrefixes] - URL prefixes to not track at all.
 * @param {boolean} [options.stripToken] - strip `access_token` from reported URLs.
 * @param {boolean} [options.pushDataLayer] - also push page_view to window.dataLayer (GTM).
 * @param {string|null} [options.scrollSelector] - what to scroll to top on route
 *   change; null disables (sciclub / cli never scrolled).
 * @param {number} [options.scrollDuration]
 * @param {boolean} [options.dropdownCleanup] - close `.data-dropdown` popups on
 *   route change (internal apps; the public shops never did this).
 */
function setupRouterTracking(router, {
  gaId,
  skipPrefixes = ['/token_login'],
  stripToken = false,
  pushDataLayer = false,
  scrollSelector = '.scroll-top-on-transition',
  scrollDuration = 300,
  dropdownCleanup = true
} = {}) {
  router.router?.on?.('routeDidChange', () => {
    // Tag every Sentry event with the current route name so otherwise-opaque
    // runtime errors ("undefined is not a function") carry a "where". Route
    // NAME only (never the URL) — no access_token / PII leaks through here.
    Sentry.setTag('route', router.currentRouteName ?? null);
    const rawUrl = router.currentURL;
    if (!rawUrl || skipPrefixes.some(prefix => rawUrl.startsWith(prefix))) {
      return;
    }
    const url = stripToken ? stripAccessToken(rawUrl) : rawUrl;

    // Guard only the analytics calls: the UI cleanup below must run even when
    // gtag is unavailable (blocked or stripped inline snippet).
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', {
        page_title: url,
        page_location: url,
        page_path: url,
        send_to: gaId
      });
    }
    if (pushDataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: url,
        page_location: stripAccessToken(window.location.href),
        page_path: url
      });
    }

    // «UI nuova»: lib/nv-scroll-restore owns the page scroll (Back restores it, MOBILE-NATIVE-PLAN W1.5)
    if (scrollSelector && document.documentElement?.dataset?.ui !== 'nuova') {
      document.querySelectorAll(scrollSelector).forEach(el => {
        el.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
    if (dropdownCleanup) {
      document.querySelectorAll('.data-dropdown').forEach(element => {
        element.classList.remove('is-open');
      });
      // `document.body` is null while the document is being torn down (the tab
      // navigating away, a print/export window closing): the routeDidChange that
      // rides along threw "Cannot read properties of null (reading 'classList')"
      // — Sentry EMBER-MEDICAL-2WZ / -2VP.
      document.body?.classList.remove('dropdown-open');
    }
  });
}

export { setupRouterTracking, stripAccessToken };
//# sourceMappingURL=router-tracking.js.map
